import React from 'react';
import PropTypes from 'prop-types';
import './PopupButton.scss';

/**
 * PopupButton Component
 * A reusable button component for popups following the new design system
 *
 * @param {string} variant - Button style variant: 'primary', 'secondary', 'danger', 'success'
 * @param {string} size - Button size: 'small', 'medium', 'large'
 * @param {boolean} fullWidth - Make button take full width
 * @param {boolean} loading - Show loading state
 * @param {boolean} disabled - Disable button
 * @param {React.ReactNode} children - Button content
 * @param {React.ReactNode} leftIcon - Icon to display on the left
 * @param {React.ReactNode} rightIcon - Icon to display on the right
 * @param {function} onClick - Click handler
 * @param {string} type - HTML button type
 * @param {string} className - Additional CSS classes
 * @param {object} style - Inline styles
 */
const PopupButton = ({
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  loading = false,
  disabled = false,
  children,
  leftIcon,
  rightIcon,
  onClick,
  type = 'button',
  className = '',
  style = {},
  ...restProps
}) => {
  const buttonClasses = [
    'popup-button',
    `popup-button--${variant}`,
    `popup-button--${size}`,
    fullWidth && 'popup-button--full-width',
    loading && 'popup-button--loading',
    disabled && 'popup-button--disabled',
    className
  ].filter(Boolean).join(' ');

  const handleClick = (e) => {
    if (disabled || loading) {
      e.preventDefault();
      return;
    }
    if (onClick) {
      onClick(e);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <>
          <span className="popup-button__loader" />
          <span className="popup-button__text">Loading...</span>
        </>
      );
    }

    return (
      <>
        {leftIcon && <span className="popup-button__icon popup-button__icon--left">{leftIcon}</span>}
        <span className="popup-button__text">{children}</span>
        {rightIcon && <span className="popup-button__icon popup-button__icon--right">{rightIcon}</span>}
      </>
    );
  };

  return (
    <button
      type={type}
      className={buttonClasses}
      style={style}
      onClick={handleClick}
      disabled={disabled || loading}
      {...restProps}
    >
      {renderContent()}
    </button>
  );
};

PopupButton.propTypes = {
  variant: PropTypes.oneOf(['primary', 'secondary', 'danger', 'success']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  fullWidth: PropTypes.bool,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
  leftIcon: PropTypes.node,
  rightIcon: PropTypes.node,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  className: PropTypes.string,
  style: PropTypes.object
};

export default PopupButton;

/**
 * Usage Examples:
 *
 * Primary Button (Confirm/Action):
 * <PopupButton variant="primary" onClick={handleConfirm}>
 *   Create Snapshot
 * </PopupButton>
 *
 * Secondary Button (Cancel):
 * <PopupButton variant="secondary" onClick={handleCancel}>
 *   Cancel
 * </PopupButton>
 *
 * Danger Button (Delete):
 * <PopupButton variant="danger" onClick={handleDelete}>
 *   Delete
 * </PopupButton>
 *
 * With Icons:
 * <PopupButton variant="primary" leftIcon={<SaveIcon />}>
 *   Save Changes
 * </PopupButton>
 *
 * Loading State:
 * <PopupButton variant="primary" loading={isLoading}>
 *   Submit
 * </PopupButton>
 *
 * Full Width:
 * <PopupButton variant="primary" fullWidth>
 *   Continue
 * </PopupButton>
 */